<script setup lang="ts">
import {reactive, ref, watch, watchEffect} from "vue";
import {getUsers as apiGetUsers} from "@/Services/Admin/admin-users";
import {saving} from "@/Composables/api";
import AutoComplete from "primevue/autocomplete";
import {Button, Dialog, InputText, Label, Select, ToggleSwitch} from "@/Components/Library";
import {Messages} from "@/Components/Forms";

const model = defineModel<boolean>("visible");

const props = defineProps<{
    currentUser?: {
        id: string;
        name: string;
        groups?: any[];
    };
}>();

const emit = defineEmits(["added"]);

const users = ref([]);
const selectedUser = ref(null);
const customName = ref(false);

const getUsers = async (searchQuery?: string, paginator?: {first: number; last: number}) => {
    console.log("paginator", paginator);
    apiGetUsers({
        offset: paginator?.first ?? null,
        limit: paginator?.last ?? null,
        filter: {
            search: searchQuery,
            person_id: null,
        },
    }).then(({data}) => {
        console.log("users", data);
        users.value = data;
    });
};

const newContact = reactive({
    person_id: null,
    first_name: null,
    last_name: null,
    email: null,
    invitation_status: "accepted",
});

const searchUsers = (event) => {
    const query = event.query;
    if (query.length === 0) {
        getUsers();
    } else {
        getUsers(query);
    }
};

const save = async () => {
    //
};

watch(
    () => model.value,
    (visible) => {
        if (visible) {
            getUsers();
        }
    },
);

watchEffect(() => {
    if (selectedUser.value?.id) {
        newContact.person_id = selectedUser.value.id;
        newContact.first_name = selectedUser.value.first_name;
        newContact.last_name = selectedUser.value.last_name;
        newContact.email = selectedUser.value.email;
    }
});
</script>
<template>
    <Dialog
        v-model:visible="model"
        modal
        :header="`Add a new contact for ${currentUser?.name}`"
        class="mx-8 max-h-dvh w-full overflow-y-auto sm:max-w-lg"
    >
        <form class="flex flex-col" @submit.prevent="save">
            <div class="mb-4 flex flex-col">
                <Label for="group" class="mb-1" required>Select a user to add as a contact</Label>
                <AutoComplete
                    input-id="user"
                    name="group"
                    v-model="selectedUser"
                    :suggestions="users"
                    optionLabel="name"
                    placeholder="Start typing to search for a user"
                    required
                    fluid
                    @complete="searchUsers"
                />
            </div>
            <div v-if="selectedUser?.id" class="flex flex-col gap-3">
                <div class="flex items-center gap-1">
                    <ToggleSwitch input-id="custom_name" v-model="customName" />
                    <Label for="custom_name">Set custom display name</Label>
                </div>
                <div class="flex flex-col">
                    <Label for="first_name" required class="mb-1">First name</Label>
                    <InputText
                        id="first_name"
                        name="first_name"
                        v-model="newContact.first_name"
                        required
                        :disabled="!customName"
                    />
                </div>
                <div class="flex flex-col">
                    <Label for="last_name" required class="mb-1">Last name</Label>
                    <InputText
                        id="last_name"
                        name="last_name"
                        v-model="newContact.last_name"
                        required
                        :disabled="!customName"
                    />
                </div>
                <div class="flex flex-col">
                    <Label for="email" required class="mb-1">Email</Label>
                    <InputText id="email" name="email" v-model="newContact.email" required disabled />
                </div>
                <div class="flex flex-col">
                    <Label for="invitation_status" class="mb-1" required>Invitation status</Label>
                    <Select
                        input-id="invitation_status"
                        :options="[
                            {label: 'Pending', value: 'pending'},
                            {label: 'Accepted', value: 'accepted'},
                            {label: 'Declined', value: 'declined'},
                        ]"
                        option-label="label"
                        option-value="value"
                        required
                        placeholder="Select an invitation status"
                        v-model="newContact.invitation_status"
                    />
                </div>
            </div>
            <span v-else>Select a user first.</span>
            <Messages form-name="admin-user-groups-add" class="mb-4" />
            <Button :loading="!!saving" class="bg-askr-green text-white" type="submit">Add</Button>
        </form>
    </Dialog>
</template>
<style scoped></style>
